import { createSlice } from '@reduxjs/toolkit';
import { addToCart, fetchCartInfo, removeCartByProductId, updateToCart } from '../thunk/cartThunk';
import {
    addItemToLocalCart,
    removeQtyFromLocalCart,
    updateCartPreference,
} from '../../utils/localCartUtils';
import { getSecureItem, setSecureItem, removeSecureItem, migrateToSecureStorage } from '../../utils/secureStorage';

// Initialize secure cart storage and migrate legacy data
migrateToSecureStorage('localCart');

const initialState = {
    cartData: getSecureItem('localCart') || null,
    loading: true,
    error: null,
    cartActionLoading: true,
    cartActionError: null,
    initialLoad: true,
};
const cartSlice = createSlice({
    name: 'cart',
    initialState,
    reducers: {
        addLocalToCart: (state, action) => {
            // Handle both old and new payload formats for backward compatibility
            const isNewFormat = action.payload && action.payload.product;
            const product = isNewFormat ? action.payload.product : action.payload;
            const quantity = isNewFormat ? action.payload.quantity : null;

            state.cartData = addItemToLocalCart(state.cartData, product, quantity);
            setSecureItem('localCart', state.cartData);
        },

        removeLocalQtyFromCart: (state, action) => {
            const { productId, removeQty } = action.payload;
            state.cartData = removeQtyFromLocalCart(state.cartData, productId, removeQty || 1);
            setSecureItem('localCart', state.cartData);
        },

        updateProductPreference: (state, action) => {
            state.cartData = updateCartPreference(state.cartData, action.payload);
            setSecureItem('localCart', state.cartData);
        },
        setInitialLoadComplete: (state) => {
            state.initialLoad = false;
        },
        removeLocalFromCart: (state, action) => {
            const productId = action.payload;

            if (state.cartData?.item_info?.[productId]) {
                delete state.cartData.item_info[productId];

                const items = Object.values(state.cartData.item_info);

                if (items.length > 0) {
                    state.cartData.cart_total_quantity = items.reduce(
                        (sum, item) => sum + item.product_quantity,
                        0,
                    );
                    state.cartData.order_total = parseFloat(
                        items.reduce((sum, item) => sum + item.product_total, 0).toFixed(2),
                    );
                    state.cartData.formatted_order_total = `$${state.cartData.order_total.toFixed(
                        2,
                    )}`;
                } else {
                    // If no items left, clear localCartData
                    state.cartData = null;
                }
            }
            setSecureItem('localCart', state.cartData);
        },

        clearLocalCart: (state) => {
            removeSecureItem('localCart');
            state.cartData = null;
        },
    },
    extraReducers: (builder) => {
        builder

            .addCase(fetchCartInfo.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchCartInfo.fulfilled, (state, action) => {
                state.loading = false;
                state.cartData = action.payload.data;
            })
            .addCase(fetchCartInfo.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload;
            })
            //add to cart
            .addCase(addToCart.pending, (state) => {
                state.cartActionLoading = true;
                state.cartActionError = null;
            })
            .addCase(addToCart.fulfilled, (state, action) => {
                state.cartActionLoading = false;
                //state.cartData = action.payload.data;
            })
            .addCase(addToCart.rejected, (state, action) => {
                state.cartActionLoading = false;
                state.cartActionError = action.payload;
            })
            //update to cart
            .addCase(updateToCart.pending, (state) => {
                state.cartActionLoading = true;
                state.cartActionError = null;
            })
            .addCase(updateToCart.fulfilled, (state, action) => {
                state.cartActionLoading = false;
                //state.cartData = action.payload.data;
            })
            .addCase(updateToCart.rejected, (state, action) => {
                state.cartActionLoading = false;
                state.cartActionError = action.payload;
            })
            //removeCartByProductId
            .addCase(removeCartByProductId.pending, (state) => {
                state.cartActionLoading = true;
                state.cartActionError = null;
            })
            .addCase(removeCartByProductId.fulfilled, (state, action) => {
                state.cartActionLoading = false;
                //state.cartData = action.payload.data;
            })
            .addCase(removeCartByProductId.rejected, (state, action) => {
                state.cartActionLoading = false;
                state.cartActionError = action.payload;
            });
    },
});

export const {
    addLocalToCart,
    removeLocalQtyFromCart,
    removeLocalFromCart,
    clearLocalCart,
    updateProductPreference,
} = cartSlice.actions;

// Selectors
export const selectCartData = (state) => state.cart.cartData;

export default cartSlice.reducer;
